import React from 'react';
import { motion } from 'framer-motion';
import globalsim from './assets/images/globalsim.png';

const SectionTitle = ({ children }) => (
  <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
    <span className="text-secondary">/</span> {children}
  </h2>
);

export default function GlobalCoalitionSim({ onBack }) {
  return (
    <div className="max-w-5xl mx-auto px-6 py-20">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-secondary hover:text-white font-mono text-sm mb-8"
      >
        ← Back to portfolio
      </button>

      <SectionTitle>Global Coalition Sim</SectionTitle>

      <div className="grid gap-12 lg:grid-cols-[0.95fr_1fr] mb-14">
        <div className="rounded-3xl overflow-hidden border border-slate-700 bg-slate-800 aspect-[16/9] w-full">
          <img
            src={globalsim}
            alt="Global Coalition Sim interface preview"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="space-y-6 text-slate-400">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-lg leading-8"
          >
            Global Coalition Sim is an interactive decision game that challenges players to balance economic, social, and environmental systems. It was built as a collaborative university project using React TypeScript, Tailwind CSS, Flask, and PostgreSQL.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg leading-8"
          >
            I owned the frontend interface, animations, and audio design. The gameplay experience emphasizes clarity and feedback, helping users understand how each decision impacts world stability.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg leading-8"
          >
            Play it here (password: frontend123): <a href="https://wxyz7.itch.io/sdg-decision-game" target="_blank" rel="noreferrer" className="text-primary hover:underline">https://wxyz7.itch.io/sdg-decision-game</a> and view source code on <a href="https://github.com/hshrN/SENG-401-Project" target="_blank" rel="noreferrer" className="text-primary hover:underline">GitHub</a>.
          </motion.p>
          <div className="rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <h3 className="text-xl font-semibold text-white mb-3">Highlights</h3>
            <ul className="list-disc list-inside space-y-2 text-slate-400">
              <li>Dynamic scenario-based UI with responsive decision panels.</li>
              <li>Audio design layered with music and SFX for immersive gameplay.</li>
              <li>Backend integration for persistent state and multiplayer-style sessions.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-700 bg-slate-900 p-8 space-y-6">
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">My role</h3>
          <p className="text-slate-400 leading-7">
            I built the user-facing simulation screens, developing responsive layout logic and creating polished transitions for change effects. The interface also supports active feedback loops, showing players the immediate impact of their choices.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <h4 className="text-white font-semibold mb-3">Visual design</h4>
            <p className="text-slate-400 leading-7">
              I designed the game to feel modern and accessible, using strong color contrast, intuitive cards, and subtle motion to guide players through decisions.
            </p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3">Technical work</h4>
            <p className="text-slate-400 leading-7">
              I implemented the front-end logic for scenario updates, audio triggers, and animation timing, ensuring the game felt responsive even during complex state changes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
